import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Send, Trash2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const formatMessage = (text) => {
  // Convert **bold** to <strong>, *italic* to <em>, bullet points
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#00FF41]">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^(\s*)[-•]\s/gm, '$1<span class="text-[#00FF41] mr-1">-</span>');
};

const MessageBubble = ({ role, content, idx }) => {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap break-words bg-[#003B00] text-[#00FF41] rounded-t-lg rounded-bl-lg" data-testid={`assistant-msg-user-${idx}`}>
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div
        className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap break-words bg-[#111] text-gray-200 border border-[#003B00]/30 rounded-t-lg rounded-br-lg leading-relaxed"
        data-testid={`assistant-msg-assistant-${idx}`}
        dangerouslySetInnerHTML={{ __html: formatMessage(content) }}
      />
    </div>
  );
};

const AssistantChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load history on first open
  useEffect(() => {
    if (open && !historyLoaded) {
      (async () => {
        try {
          const res = await axios.get(`${API_V2}/assistant/history`, { withCredentials: true });
          const msgs = (res.data?.messages || []).map(m => ({ role: m.role, content: m.content }));
          if (msgs.length) setMessages(msgs);
        } catch {}
        setHistoryLoaded(true);
      })();
    }
  }, [open, historyLoaded]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API_V2}/assistant/chat`, { message: text }, { withCredentials: true, timeout: 30000 });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data?.response || 'No response.' }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to get response.';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
    }
    setLoading(false);
  }, [input, loading]);

  const clearHistory = async () => {
    try {
      await axios.delete(`${API_V2}/assistant/history`, { withCredentials: true });
      setMessages([]);
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating bubble
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-16 right-5 z-50 flex items-center gap-2 bg-[#003B00] border border-[#00FF41] px-3 py-2 rounded-full shadow-[0_0_20px_rgba(0,255,65,0.3)] hover:shadow-[0_0_30px_rgba(0,255,65,0.5)] hover:bg-[#004d00] transition-all group"
        data-testid="assistant-bubble"
      >
        <MessageSquare className="h-4 w-4 text-[#00FF41] group-hover:scale-110 transition-transform" />
        <span className="text-xs font-bold text-[#00FF41] tracking-wider uppercase">AI Assist</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-16 right-5 z-50 w-[380px] max-w-[calc(100vw-40px)] h-[520px] max-h-[calc(100vh-80px)] bg-[#0a0a0a] border border-[#003B00] shadow-[0_0_40px_rgba(0,255,65,0.15)] flex flex-col"
      data-testid="assistant-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#003B00] bg-[#050505] shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[#00FF41]" />
          <span className="text-sm font-bold text-[#00FF41] tracking-wider uppercase">BitBot AI</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearHistory}
            className="p-1 text-gray-600 hover:text-red-400 transition-colors"
            title="Clear history"
            data-testid="assistant-clear"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-gray-600 hover:text-white transition-colors"
            data-testid="assistant-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-3 min-h-0">
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <MessageSquare className="h-8 w-8 text-[#003B00] mx-auto mb-2" />
            <p className="text-sm text-gray-500">Ask me about trading strategies, app features, or your portfolio.</p>
            <div className="mt-3 space-y-1">
              {['Why did my bot sell BTC?', 'Explain RSI strategy', 'How do I reduce fees?'].map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="block w-full text-left text-xs text-[#008F11] hover:text-[#00FF41] px-2 py-1 hover:bg-[#001a00] rounded transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} content={m.content} idx={i} />
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#111] border border-[#003B00]/30 rounded-t-lg rounded-br-lg px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 text-[#00FF41] animate-spin" />
              <span className="text-xs text-gray-500">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-[#003B00] p-2 bg-[#050505]">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            maxLength={2000}
            disabled={loading}
            className="flex-1 bg-[#0a0a0a] border border-[#003B00] text-sm text-white placeholder-gray-600 px-3 py-2 outline-none focus:border-[#00FF41] transition-colors"
            data-testid="assistant-input"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="p-2 bg-[#003B00] text-[#00FF41] border border-[#003B00] hover:bg-[#004d00] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="assistant-send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantChat;
